import { useState } from 'react';
import { toast } from 'react-toastify';

import type { DocumentDownloadLogFilters, DownloadLogExportFormat, DownloadLogExportResult } from '../../api/downloadLogs';

interface DownloadLogExportButtonProps {
  filters: DocumentDownloadLogFilters;
  onExport: (format: DownloadLogExportFormat, filters: DocumentDownloadLogFilters) => Promise<DownloadLogExportResult>;
}

const FORMATS: DownloadLogExportFormat[] = ['csv', 'pdf'];
const FORMAT_LABELS: Record<DownloadLogExportFormat, string> = { csv: 'Export CSV', pdf: 'Export PDF' };

const triggerBlobDownload = (blob: Blob, headers: Headers, fallbackName: string) => {
  const disposition = headers.get('Content-Disposition') || '';
  const match = /filename="?([^"]+)"?/.exec(disposition);
  const filename = match?.[1] || fallbackName;

  const url = window.URL.createObjectURL(blob);
  const a = window.document.createElement('a');
  a.href = url;
  a.download = filename;
  window.document.body.appendChild(a);
  a.click();
  window.URL.revokeObjectURL(url);
  window.document.body.removeChild(a);
};

/**
 * CSV/PDF export trigger. Small results download immediately; large ones are
 * queued server-side and delivered by email — there is no status-polling
 * endpoint, so a one-shot toast is the only feasible feedback for that path.
 */
export const DownloadLogExportButton = ({ filters, onExport }: DownloadLogExportButtonProps) => {
  const [exporting, setExporting] = useState<DownloadLogExportFormat | null>(null);

  const handleExport = async (format: DownloadLogExportFormat) => {
    setExporting(format);
    try {
      const result = await onExport(format, filters);

      if ('blob' in result) {
        triggerBlobDownload(result.blob, result.headers, `download-log.${format}`);
        toast.success('Download log exported successfully');
      } else {
        toast.info(result.message || "Export is being generated — we'll email you a link when it's ready.");
      }
    } catch (error) {
      console.error('Failed to export download log:', error);
      toast.error('Failed to export download log');
    } finally {
      setExporting(null);
    }
  };

  return (
    <div className="flex gap-2">
      {FORMATS.map(format => (
        <button
          key={format}
          onClick={() => handleExport(format)}
          disabled={exporting !== null}
          className="rounded-md border border-[#eaeaea] px-3 py-1.5 text-sm text-[#444] hover:bg-[#fafafa] disabled:opacity-50"
        >
          {exporting === format ? 'Exporting…' : FORMAT_LABELS[format]}
        </button>
      ))}
    </div>
  );
};
